import React, { useRef, useState, useMemo, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Stars, Environment } from "@react-three/drei";
import * as THREE from "three";
import Angel4 from "./angel4";
import RingTerminal from "./ringterminal";
import "./ontology.css";

// 初始默认标签
const DEFAULT_LABELS = [
  "Each tap is a terminal",
  "Click to open",
  "Each tap is a terminal",
  "Hold to provision",
  "Each tap is a terminal",
  "Drag to pan",
  "Each tap is a terminal",
];

function SceneAndLabels({ items, onUpdate }) {
  // 根据 items.length 重新计算半径／高度／步长
  const planets = useMemo(() => {
    const R = 2 + items.length * 1.8;
    const H = 0.5 + (items.length - 1) * 0.2;
    const STEP = (2 * Math.PI) / items.length;
    return items.map((it, i) => ({
      id: it.id,
      label: it.label,
      radius: R,
      height: H,
      angle0: i * STEP,
      speed: 0.05 / items.length,
    }));
  }, [items]);

  useFrame(({ camera, size, clock }) => {
    const positions = planets.map(p => {
      const t = clock.getElapsedTime() * p.speed;
      const a = p.angle0 + t;
      const world = new THREE.Vector3(
        p.radius * Math.cos(a),
        p.height,
        p.radius * Math.sin(a)
      );
      world.project(camera);
      return {
        id: p.id,
        label: p.label,
        x: (world.x * 0.5 + 0.5) * size.width,
        y: (-world.y * 0.5 + 0.5) * size.height,
      };
    });
    // 简易避碰
    const minDist = 60;
    for (let pass = 0; pass < 3; pass++) {
      for (let i = 0; i < positions.length; i++) {
        for (let j = i + 1; j < positions.length; j++) {
          const A = positions[i], B = positions[j];
          const dx = B.x - A.x, dy = B.y - A.y;
          const d = Math.hypot(dx, dy) || 0.001;
          if (d < minDist) {
            const ov = (minDist - d) / 2, ux = dx / d, uy = dy / d;
            A.x -= ux * ov; A.y -= uy * ov;
            B.x += ux * ov; B.y += uy * ov;
          }
        }
      }
    }
    onUpdate(positions);
  });

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5,5,5]} intensity={1} />
      <Environment preset="sunset" background={false} />
      <Stars radius={100} depth={50} count={2000} factor={4} fade />
      {/* 外环轨道 */}
      <mesh position={[0, planets[0]?.height, 0]} rotation={[-Math.PI/2,0,0]}>
        <torusGeometry args={[2 + items.length * 1.8, 0.02, 16, 128]} />
        <meshBasicMaterial color="#88aaff" transparent opacity={0.25} />
      </mesh>
    </>
  );
}

export default function Ontology3D() {
  // items = [{id,label}]
  const [items, setItems] = useState(() => {
    // 初始 id 生成
    return DEFAULT_LABELS.map((label, i) => ({
      id: `outer-${i}`,
      label,
    }));
  });

  const [labels, setLabels] = useState([]);     // 页面上计算出的 x/y
  const [disableDrag, setDisableDrag] = useState(false);
  const controlsRef = useRef(null);

  // 背景锁定事件
  useEffect(() => {
    const h = e => setDisableDrag(!e.detail);
    window.addEventListener("bg-interactive", h);
    return () => window.removeEventListener("bg-interactive", h);
  }, []);

  // 回调：改名
  const handleRename = (newName, oldName) => {
    setItems(items.map(it => it.label === oldName ? {...it,label:newName} : it));
  };
  // 回调：上下移
  const handleReorder = (label, dir) => {
    const idx = items.findIndex(it=>it.label===label);
    const swap = dir==="up"?idx-1:idx+1;
    if (idx<0||swap<0||swap>=items.length) return;
    const next = [...items];
    [next[idx],next[swap]] = [next[swap],next[idx]];
    setItems(next);
  };
  // 回调：新增
  const handleAdd = () => {
    const newId = `outer-${Date.now()}`;
    setItems([...items, {id:newId,label:"New Tab"}]);
  };

  return (
    <>
      <Angel4 />

      <Canvas
        camera={{position:[0,6,12],fov:55}}
        style={{position:"absolute",inset:0,pointerEvents:disableDrag?"none":"auto"}}
      >
        <OrbitControls
          ref={controlsRef}
          makeDefault enableDamping enabled={!disableDrag}
        />
        <SceneAndLabels items={items} onUpdate={setLabels} />
      </Canvas>

      <RingTerminal
        labels={labels}
        onRename={handleRename}
        onReorder={handleReorder}
        onAdd={handleAdd}
      />
    </>
  );
}
